<script setup>
defineOptions({ name: "SiteNavbar" });
import { ref, onMounted, onUnmounted } from "vue";
import { useRouter, RouterLink } from "vue-router";
import dashboardPreview from "@/assets/dashboard-preview.webp";

const router = useRouter();
const isOpen = ref(false);
const activeDropdown = ref(null);

const portalUrl =
  import.meta.env?.VITE_PORTAL_URL || "https://portal.kalisistemas.com.br";

// Medir o container interno do header para alinhar o dropdown à largura do site
const navContainer = ref(null);
const navBounds = ref({ left: 0, width: 0 });
const updateNavBounds = () => {
  const el = navContainer.value;
  if (el) {
    const rect = el.getBoundingClientRect();
    navBounds.value.left = Math.round(rect.left);
    navBounds.value.width = Math.round(rect.width);
  }
};

onMounted(() => {
  updateNavBounds();
  window.addEventListener("resize", updateNavBounds);
});

onUnmounted(() => {
  window.removeEventListener("resize", updateNavBounds);
});

const menuItems = [
  {
    text: "Nossas soluções",
    hasDropdown: true,
    sections: [
      {
        title: "ERPs",
        items: [
          {
            name: "Kali Gestão",
            description: "Solução completa para gestão empresarial",
            to: "/solucao/kali-gestao",
            badge: null,
          },
          {
            name: "Kali Agro",
            description: "Solução especializada para agronegócio",
            to: "/solucao/kali-agro",
            badge: null,
          },
          {
            name: "Kali Food",
            description: "Solução completa para bares e restaurantes",
            to: "/solucao/kali-food",
            badge: null,
          },
          {
            name: "Kali Clínica",
            description:
              "Solução completa para clínicas médicas e odontológicas",
            to: "/solucao/kali-clinica",
            badge: null,
          },
          {
            name: "Kali Salão",
            description: "Solução completa para salões de beleza e barbearia",
            to: "/solucao/kali-salao",
            badge: null,
          },
          {
            name: "Kali Studio",
            description:
              "Solução completa para estúdios de tatuagem e piercing",
            to: "/solucao/kali-studio",
            badge: null,
          },
          {
            name: "Kali Pet",
            description:
              "Solução completa para pet shops e clínicas veterinárias",
            to: "/solucao/kali-pet",
            badge: null,
          },
          {
            name: "Kali Shop",
            description: "Solução completa para e-commerce",
            to: "/solucao/kali-shop",
            badge: null,
          },
        ],
      },
      {
        title: "Módulos adicionais",
        items: [
          {
            name: "Boleto",
            description: "Emissão e controle de cobranças e vencimentos",
            to: "/boletos",
            badge: null,
          },
          {
            name: "WhatsApp",
            description: "Atendimento e mensagens integradas ao ERP",
            to: "/whatsapp",
            badge: null,
          },
          {
            name: "Assinador Digital",
            description: "Assinatura de documentos com praticidade",
            to: "/assinador-digital",
            badge: null,
          },
          {
            name: "Lote",
            description: "Rastreabilidade e controle por validade",
            to: "/lote",
            badge: null,
          },
          {
            name: "Contrato",
            description: "Gestão de contratos e recorrências",
            to: "/contrato",
            badge: null,
          },
          {
            name: "PIX",
            description: "Recebimento rápido e organizado",
            to: "/pix",
            badge: null,
          },
          {
            name: "Promoção",
            description: "Campanhas e condições promocionais",
            to: "/promocao",
            badge: null,
          },
        ],
      },
      {
        title: "Serviços Digitais",
        items: [
          {
            name: "Certificado Digital",
            description: "Adquira o Certificado digital para sua empresa",
            to: "/certificado-digital",
            badge: null,
          },
        ],
      },
      {
        title: "Destaque",
        items: [],
        featured: {
          title: "Kali Gestão",
          subtitle: "DESTAQUE",
          description: "Solução completa para gestão empresarial",
          longDescription:
            "A Kali Gestão é uma solução completa para gestão empresarial, que abrange todos os aspectos da administração e operação de uma empresa.",
          buttonText: "Saiba mais",
          to: "/solucao/kali-gestao",
          image: dashboardPreview,
        },
      },
    ],
  },
  {
    text: "Segmentos",
    hasDropdown: true,
    isSegments: true,
    // Dados para o mega-menu
    segments: [
      {
        title: "MATERIAL DE CONSTRUÇÃO",
        description:
          "A Kali oferece bases sólidas para a construção do seu sucesso!",
        to: "/segmento/material-de-construcao",
      },
      {
        title: "MODA",
        description: "O ERP sob medida para o varejo de moda",
        to: "/segmento/moda",
      },
      {
        title: "MERCADOS",
        description: "Pequenos negócios, grandes resultados!",
        to: "/segmento/mercados",
      },
      {
        title: "AÇOUGUES",
        description: "Agilidade pensada para o segmento.",
        to: "/segmento/acougues",
      },
      {
        title: "AUTOPEÇAS E OFICINAS",
        description:
          "Gestão integrada para autopeças, oficinas e centros automotivos.",
        to: "/segmento/autopecas",
      },
    ],
    // Para o menu mobile, reaproveitar estrutura padrão
    sections: [
      {
        title: "Segmentos",
        items: [
          {
            name: "Material de construção",
            to: "/segmento/material-de-construcao",
          },
          { name: "Moda", to: "/segmento/moda" },
          { name: "Mercados e minimercados", to: "/segmento/mercados" },
          {
            name: "Açougue, casa de carnes e peixaria",
            to: "/segmento/acougues",
          },
          { name: "Padarias", to: "/segmento/padarias" },
          {
            name: "Lojas de cosméticos e perfumarias",
            to: "/segmento/cosmeticos",
          },
          {
            name: "Lojas de autopeças e oficinas mecânicas",
            to: "/segmento/autopecas",
          },
          {
            name: "Sorveterias e açaiterias",
            to: "/segmento/sorveterias",
          },
          {
            name: "Lojas de bijuterias e acessórios",
            to: "/segmento/bijuterias",
          },
        ],
      },
    ],
    cta: {
      text: "Atendemos mais de 50 segmentos.",
      buttonText: "Saiba mais",
      to: "/segmentos",
    },
  },
  {
    text: "Sobre nós",
    hasDropdown: true,
    sections: [
      {
        title: "Institucional",
        items: [
          {
            name: "Sobre a Kali",
            description: "Nossa história e missão",
            to: "/sobre-nos",
            badge: null,
          },
          {
            name: "Blog",
            description: "Artigos e novidades",
            to: "/blog",
            badge: null,
          },
          {
            name: "Trabalhe Conosco",
            description: "Faça parte da nossa equipe",
            to: "/carreiras",
            badge: null,
          },
        ],
      },
      {
        title: "Parcerias",
        items: [
          {
            name: "Casos de Sucesso",
            description: "Histórias de clientes satisfeitos",
            to: "/clientes#casos-sucesso",
            badge: null,
          },
          {
            name: "Parceiros e Integrações",
            description: "Nossos parceiros estratégicos",
            // Ainda não tem conteúdo pronto — aponta pra rota inexistente de
            // propósito (cai na 404) em vez de renderizar como link morto.
            to: "/parceiros-e-integracoes",
            badge: null,
          },
          {
            name: "Eventos",
            description: "Participe dos nossos eventos",
            to: "/eventos",
            badge: null,
          },
        ],
      },
    ],
  },
  { text: "Blog", to: "/blog" },
  { text: "Contato", to: "/contato" },
  {
    text: "Suporte",
    hasDropdown: true,
    isSupport: true,
    sections: [
      {
        title: "Canais de Atendimento",
        items: [
          {
            name: "Suporte via Chat",
            description: "Atendimento online em tempo real",
            to: `${portalUrl}/atendimento`,
            badge: null,
            isButton: true,
            buttonColor: "bg-[#1e7ba8]",
          },
          {
            name: "Suporte via WhatsApp",
            description: "Atendimento pelo WhatsApp",
            to: "/suporte#whatsapp",
            badge: null,
            isButton: true,
            buttonColor: "bg-[#25d366]",
          },
        ],
      },
      {
        title: "Horários de Atendimento",
        items: [
          {
            name: "Segunda à Sexta-feira das 8:00 às 12:00 e 14:00 às 18:00*",
            description: "",
            to: "/suporte#horarios",
            badge: null,
            isTitle: true,
          },
          {
            name: "Sábado das 9:00 às 12:00*",
            description: "",
            to: "/suporte#horarios",
            badge: null,
            isTitle: true,
          },
          {
            name: "*Horário de Brasília",
            description: "",
            to: "/suporte#horarios",
            badge: null,
            isNote: true,
          },
        ],
      },
      {
        title: "Mais",
        items: [
          { name: "Clique aqui para Suporte", to: "/suporte", isCta: true },
          { name: "Atualizações" },
          { name: "Canais de Atendimento", to: "/suporte#canais" },
          { name: "Implantação" },
          { name: "Treinamentos" },
          { name: "Consultorias" },
        ],
      },
    ],
  },
];

// Considera ativo tanto o caminho exato quanto qualquer sub-rota dele
// (ex.: "/blog" continua destacado em "/blog/algum-post").
const isActive = (to) => {
  if (!to) return false;
  const current = router.currentRoute.value.path;
  return current === to || current.startsWith(`${to}/`);
};

const navigateTo = (to) => {
  if (!to) return;
  router
    .push(to)
    .then(() => {
      try {
        window.scrollTo({ top: 0, behavior: "instant" });
      } catch {
        window.scrollTo(0, 0);
      }
    })
    .finally(() => {
      isOpen.value = false;
      closeDropdown();
    });
};

const handleScheduleDemo = () => {
  const fire = () => window.dispatchEvent(new CustomEvent("open-demo-modal"));
  isOpen.value = false;
  closeDropdown();
  fire();
};

const toggleDropdown = (index) => {
  cancelClose();
  activeDropdown.value = activeDropdown.value === index ? null : index;
};

const openDropdown = (index) => {
  cancelClose();
  activeDropdown.value = index;
};

// Atraso para fechar e evitar fechamento ao transitar do botão para o dropdown
let closeTimeout = null;
const scheduleClose = () => {
  if (closeTimeout) clearTimeout(closeTimeout);
  closeTimeout = setTimeout(() => {
    activeDropdown.value = null;
    closeTimeout = null;
  }, 150);
};
const cancelClose = () => {
  if (closeTimeout) {
    clearTimeout(closeTimeout);
    closeTimeout = null;
  }
};

const closeDropdown = () => {
  activeDropdown.value = null;
};
</script>

<template>
  <nav class="fixed top-0 z-50 w-full bg-white shadow-lg">
    <div class="px-4 mx-auto max-w-7xl" ref="navContainer">
      <div class="flex justify-between h-16">
        <div class="flex items-center">
          <div class="flex-shrink-0">
            <RouterLink to="/" class="flex items-center space-x-2 cursor-pointer">
              <img class="w-auto h-8" src="@/assets/logo.png" alt="Kali Sistemas" width="160" height="32" />
            </RouterLink>
          </div>
        </div>

        <div class="hidden lg:flex lg:items-center">
          <div class="flex items-center space-x-4">
            <div v-for="(item, index) in menuItems" :key="index" class="relative">
              <!-- Menu item with dropdown -->
              <div v-if="item.hasDropdown" class="relative" :id="`dropdown-${index}`" @mouseenter="openDropdown(index)"
                @mouseleave="scheduleClose">
                <button @click="toggleDropdown(index)"
                  class="group menu-item-underline relative inline-flex items-center px-2 py-2 text-sm font-bold text-gray-700 hover:text-[#006086] focus:outline-none transition-all duration-300"
                  :class="{ 'text-[#006086] active': activeDropdown === index }"
                  aria-haspopup="true" :aria-expanded="activeDropdown === index">
                  {{ item.text }}
                  <svg class="ml-1.5 h-4 w-4 group-hover:text-[#006086] transition-colors duration-150"
                    :class="{ 'text-[#006086]': activeDropdown === index }" viewBox="0 0 20 20" fill="currentColor">
                    <path fill-rule="evenodd"
                      d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                      clip-rule="evenodd" />
                  </svg>
                </button>

                <!-- Mega Menu Dropdown -->
                <div v-show="activeDropdown === index"
                  class="overflow-hidden fixed top-16 z-50 bg-white rounded-b-xl border-t border-gray-200 shadow-xl mega-menu-enter"
                  :style="item.text === 'Sobre nós'
                    ? {
                      left:
                        navBounds.left + navBounds.width / 2 - 360 + 'px',
                      width: '720px',
                    }
                    : {
                      left: navBounds.left + 'px',
                      width: navBounds.width + 'px',
                    }
                    " @mouseenter="cancelClose" @mouseleave="scheduleClose">
                  <div class="px-8 py-8 bg-white">
                    <!-- Layout para menu "Nossas soluções" -->
                    <div v-if="item.text === 'Nossas soluções'">
                      <!-- Lado esquerdo: Seções de produtos -->
                      <div class="grid grid-cols-[1fr_1fr_1fr_1.6fr] gap-6 mb-4">
                        <!-- Exibir apenas as 3 primeiras seções (ERPs, Módulos adicionais, Serviços Digitais) -->
                        <div v-for="(section, sectionIndex) in item.sections.slice(
                          0,
                          3,
                        )" :key="sectionIndex" class="col-span-1 pr-6" :class="{ 'border-r border-gray-200': sectionIndex < 2 }">
                          <h3 class="mb-4 text-sm font-bold tracking-wider text-[#006086] uppercase">
                            {{ section.title }}
                          </h3>
                          <ul class="space-y-2">
                            <li v-for="(subItem, subIndex) in section.items" :key="subIndex" class="group">
                              <RouterLink :to="subItem.to"
                                class="block px-2 py-1 rounded-md transition-colors duration-150 cursor-pointer hover:bg-gray-50 group"
                                @click="closeDropdown()">
                                <div class="flex gap-1.5 items-center">
                                  <div>
                                    <p class="text-sm font-medium text-gray-700 group-hover:text-[#006086]">
                                      {{ subItem.name }}
                                    </p>
                                  </div>
                                  <span v-if="subItem.badge"
                                    class="px-2 py-0.5 text-xs font-bold text-gray-900 uppercase rounded-md shadow-sm bg-kali-gold">{{
                                      subItem.badge }}</span>
                                </div>
                              </RouterLink>
                            </li>
                          </ul>
                        </div>

                        <!-- Lado direito: Seção de destaque (sempre fixo) -->
                        <div v-if="
                          item.sections &&
                          item.sections[3] &&
                          item.sections[3].featured
                        " class="col-span-1 p-6 bg-gray-50 rounded-lg border border-gray-200 shadow-sm">
                          <div class="text-left">
                            <div class="mb-3">
                              <img v-if="item.sections[3].featured.image" :src="item.sections[3].featured.image"
                                alt="Kali Gestão" loading="lazy" class="object-cover mb-4 w-full h-32 rounded-md" />
                              <div v-else
                                class="flex justify-center items-center mb-4 w-full h-32 bg-gradient-to-br from-orange-400 to-orange-600 rounded-md">
                                <svg class="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                                  <path
                                    d="M20 2H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h4l4 4 4-4h4c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-2 12H6v-2h12v2zm0-3H6V9h12v2zm0-3H6V6h12v2zm0-3H6V6h12v2zm0-3H6V6h12v2zm0-3H6V6h12v2zm0-3H6V6h12v2zm0-3H6V6h12v2zm0-3H6V6h12v2zm0-3H6V6h12v2zm0-3H6V6h12v2z" />
                                </svg>
                              </div>
                              <span
                                class="inline-block px-2 py-1 mb-2 text-xs font-bold text-gray-900 rounded-full bg-kali-gold">{{
                                  item.sections[3].featured.subtitle }}</span>
                            </div>
                            <h4 class="mb-1 text-sm font-bold text-gray-900">
                              {{ item.sections[3].featured.title }}
                            </h4>
                            <p class="mb-1 text-sm text-gray-700">
                              {{ item.sections[3].featured.description }}
                            </p>
                            <p class="mb-4 text-xs leading-relaxed text-gray-600">
                              {{ item.sections[3].featured.longDescription }}
                            </p>
                            <RouterLink :to="item.sections[3].featured.to"
                              class="inline-flex items-center px-4 py-2 text-sm font-bold text-gray-900 bg-kali-gold hover:bg-[#d5ac2f] rounded-md shadow transition cursor-pointer"
                              @click="closeDropdown()">
                              {{ item.sections[3].featured.buttonText }}
                              <svg class="ml-2 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7">
                                </path>
                              </svg>
                            </RouterLink>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- Layout especial para menu Suporte -->
                    <div v-else-if="item.isSupport">
                      <div class="grid grid-cols-[1.5fr_1fr_1fr] gap-8">
                        <div class="pr-6 border-r border-gray-200">
                          <div class="mb-6">
                            <p class="mb-2 text-sm text-gray-600">
                              Precisa de Suporte? Nossos analistas estão prontos
                              para lhe atender.
                            </p>
                            <p class="text-sm text-gray-600">
                              Acesse:
                              <a href="mailto:suporte@kalisistemas.com.br"
                                class="text-[#006086] hover:underline">suporte@kalisistemas.com.br</a>
                            </p>
                          </div>
                          <h3 class="mb-4 text-sm font-bold tracking-wider text-[#006086] uppercase">
                            {{ item.sections[0].title }}
                          </h3>
                          <div class="grid grid-cols-2 gap-6">
                            <component :is="btn.name.includes('WhatsApp') || btn.name.includes('Chat')
                              ? 'a'
                              : 'router-link'
                              " v-for="(
btn, bIndex
                              ) in item.sections[0].items.filter(
  (i) => i.isButton,
)" :key="bIndex" v-bind="btn.name.includes('WhatsApp')
  ? { href: 'https://wa.me/5579991389415?text=Ol%C3%A1%2C%20preciso%20de%20suporte', target: '_blank' }
  : btn.name.includes('Chat')
    ? { href: btn.to, target: '_blank', rel: 'noopener' }
    : { to: btn.to }
  " @click="closeDropdown"
                              class="flex flex-col items-center justify-center h-36 rounded-lg text-white shadow-md transition transform hover:scale-[1.02] hover:shadow-lg cursor-pointer"
                              :class="btn.buttonColor">
                              <svg v-if="btn.name.includes('Chat')" class="mb-3 w-16 h-16" fill="none"
                                viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
                                <path stroke-linecap="round" stroke-linejoin="round"
                                  d="M2.25 12.76c0 1.6 1.123 2.994 2.707 3.227 1.087.16 2.185.283 3.293.369V21l4.076-4.076a1.526 1.526 0 011.037-.443 48.282 48.282 0 005.68-.494c1.584-.233 2.707-1.626 2.707-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394 48.394 0 0012 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018z" />
                              </svg>
                              <svg v-else-if="btn.name.includes('WhatsApp')" class="mb-3 w-16 h-16" viewBox="0 0 24 24"
                                fill="currentColor" stroke="none">
                                <path
                                  d="M12.04 2c-5.46 0-9.91 4.45-9.91 9.91 0 1.75.46 3.45 1.32 4.95L2.05 22l5.25-1.38c1.45.79 3.08 1.21 4.74 1.21 5.46 0 9.91-4.45 9.91-9.91 0-2.65-1.03-5.14-2.9-7.01A9.816 9.816 0 0012.04 2zM12.05 20.21c-1.5 0-2.97-.39-4.26-1.15l-.3-.18-3.16.83.84-3.07-.2-.31a8.208 8.208 0 01-1.26-4.41c0-4.54 3.7-8.24 8.24-8.24 2.2 0 4.27.86 5.82 2.42a8.183 8.183 0 012.41 5.83c.02 4.54-3.68 8.23-8.22 8.23zm4.52-6.16c-.25-.12-1.47-.72-1.69-.81-.23-.08-.39-.12-.56.12-.17.25-.64.81-.78.97-.14.17-.29.19-.54.06-.25-.12-1.05-.39-1.99-1.23-.74-.66-1.23-1.47-1.38-1.72-.14-.25-.02-.38.11-.51.11-.11.25-.29.37-.43s.17-.25.25-.41c.08-.17.04-.31-.02-.43s-.56-1.34-.76-1.84c-.2-.48-.41-.42-.56-.43h-.48c-.17 0-.43.06-.66.31-.22.25-.86.85-.86 2.07 0 1.22.89 2.4 1.01 2.56.12.17 1.75 2.67 4.23 3.76.59.26 1.05.41 1.41.52.59.19 1.13.16 1.56.1.48-.07 1.47-.6 1.67-1.18.21-.58.21-1.07.14-1.18s-.22-.16-.47-.28z" />
                              </svg>
                              <span class="text-base font-medium text-white">{{
                                btn.name
                              }}</span>
                            </component>
                          </div>
                        </div>

                        <div class="px-4 border-r border-gray-200">
                          <RouterLink :to="item.sections[2].items.find((i) => i.isCta)?.to
                            "
                            class="block px-2 py-2 mb-2 text-sm font-bold tracking-wider text-[#006086] uppercase rounded-md transition-colors duration-150 hover:bg-gray-50"
                            @click="closeDropdown()">
                            {{
                              item.sections[2].items.find((i) => i.isCta)?.name
                            }}
                          </RouterLink>
                          <ul class="space-y-1">
                            <li v-for="(
supportItem, sIndex
                              ) in item.sections[2].items.filter(
  (i) => !i.isCta,
)" :key="sIndex">
                              <a v-if="supportItem.to" :href="supportItem.to" @click.prevent="navigateTo(supportItem.to)"
                                class="block px-2 py-2 text-sm font-medium text-gray-700 rounded-md transition-colors duration-150 cursor-pointer hover:bg-gray-50 hover:text-[#006086]">
                                {{ supportItem.name }}
                              </a>
                              <div v-else
                                class="block px-2 py-2 text-sm font-medium text-gray-700 rounded-md transition-colors duration-150 hover:bg-gray-50">
                                {{ supportItem.name }}
                              </div>
                            </li>
                          </ul>
                        </div>

                        <div class="pl-4">
                          <h3 class="mb-4 text-sm font-bold tracking-wider text-[#006086] uppercase">
                            {{ item.sections[1].title }}
                          </h3>
                          <div class="space-y-2">
                            <div v-for="(subItem, subIndex) in item.sections[1]
                              .items" :key="subIndex">
                              <p v-if="subItem.isTitle" class="mt-3 mb-1 text-sm font-semibold text-gray-800">
                                {{ subItem.name }}
                              </p>
                              <p v-else-if="subItem.isNote" class="mt-3 text-xs text-gray-500">
                                {{ subItem.name }}
                              </p>
                              <p v-else class="text-sm leading-relaxed text-gray-600">
                                {{ subItem.name }}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- Layout especial para menu Segmentos -->
                    <div v-else-if="item.isSegments">
                      <div class="space-y-8">
                        <!-- Cards em 5 colunas -->
                        <div class="grid grid-cols-5 gap-6">
                          <div v-for="(seg, i) in item.segments" :key="i"
                            class="flex flex-col justify-between pr-6 border-r border-gray-200 last:border-r-0">
                            <div>
                              <h3 class="text-sm font-bold text-[#006086] mb-2">
                                {{ seg.title }}
                              </h3>
                              <p class="text-sm leading-relaxed text-gray-600">
                                {{ seg.description }}
                              </p>
                            </div>
                            <div class="mt-4">
                              <a :href="seg.to" @click.prevent="navigateTo(seg.to)"
                                class="inline-flex items-center justify-center px-4 py-2 text-sm font-bold text-[#006086] border border-[#006086] rounded-md bg-white hover:bg-[#e6f0f4] hover:text-[#004d6b] transition cursor-pointer">
                                Saiba mais
                                <svg class="ml-2 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                    d="M9 5l7 7-7 7"></path>
                                </svg>
                              </a>
                            </div>
                          </div>
                        </div>

                        <!-- Faixa roxa com CTA -->
                        <div
                          class="rounded-md bg-[#006086] text-white pl-6 pr-4 py-4 flex items-center justify-between shadow-sm">
                          <p class="text-base font-bold">{{ item.cta.text }}</p>
                          <a :href="item.cta.to" @click.prevent="navigateTo(item.cta.to)"
                            class="inline-flex items-center px-4 py-2 rounded-md text-sm font-bold bg-white text-[#006086] hover:bg-gray-100 transition shadow-sm cursor-pointer">
                            {{ item.cta.buttonText }}
                          </a>
                        </div>
                      </div>
                    </div>

                    <!-- Layout especial para Sobre nós -->
                    <div v-else-if="item.text === 'Sobre nós'">
                      <div class="grid grid-cols-2 gap-6">
                        <div v-for="(section, sectionIndex) in item.sections" :key="sectionIndex"
                          class="col-span-1 pr-6" :class="{ 'border-r border-gray-200': sectionIndex < 1 }">
                          <h3 class="mb-3 text-sm font-bold tracking-wider text-[#006086] uppercase">
                            {{ section.title }}
                          </h3>
                          <ul class="space-y-1">
                            <li v-for="(subItem, subIndex) in section.items" :key="subIndex" class="group">
                              <a :href="subItem.to" @click.prevent="navigateTo(subItem.to)"
                                class="block px-2 py-2 rounded-md transition-colors duration-150 cursor-pointer hover:bg-gray-50 group">
                                <div class="flex justify-between items-center">
                                  <div class="flex-1">
                                    <p class="text-sm font-medium text-gray-700 group-hover:text-[#006086]">
                                      {{ subItem.name }}
                                    </p>
                                    <p v-if="subItem.description" class="mt-0.5 text-xs text-gray-500">
                                      {{ subItem.description }}
                                    </p>
                                  </div>
                                  <span v-if="subItem.badge"
                                    class="px-2 py-0.5 ml-1 text-xs font-semibold text-gray-900 rounded-full bg-kali-gold">{{
                                      subItem.badge }}</span>
                                </div>
                              </a>
                            </li>
                          </ul>
                        </div>
                      </div>
                    </div>

                    <!-- Layout para outros menus com 3 seções -->
                    <div v-else>
                      <div class="grid grid-cols-3 gap-8">
                        <div v-for="(section, sectionIndex) in item.sections" :key="sectionIndex" class="col-span-1">
                          <h3 class="mb-3 text-sm font-bold tracking-wider text-[#006086] uppercase">
                            {{ section.title }}
                          </h3>
                          <ul class="space-y-1">
                            <li v-for="(subItem, subIndex) in section.items" :key="subIndex" class="group">
                              <router-link :to="subItem.to" @click="closeDropdown"
                                class="block px-2 py-2 rounded-md transition-colors duration-150 cursor-pointer hover:bg-gray-50 group">
                                <div class="flex justify-between items-center">
                                  <div class="flex-1">
                                    <p class="text-sm font-medium text-gray-700 group-hover:text-[#006086]">
                                      {{ subItem.name }}
                                    </p>
                                    <p v-if="subItem.description" class="mt-0.5 text-xs text-gray-500">
                                      {{ subItem.description }}
                                    </p>
                                  </div>
                                  <span v-if="subItem.badge"
                                    class="px-2 py-0.5 ml-1 text-xs font-semibold text-gray-900 rounded-full bg-kali-gold">{{
                                      subItem.badge }}</span>
                                </div>
                              </router-link>
                            </li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Regular menu item -->
              <a v-else @click.prevent="navigateTo(item.to)" :href="item.to"
                class="menu-item-underline relative inline-flex items-center px-2 py-2 text-sm font-bold text-gray-700 hover:text-[#006086] transition-all duration-300"
                :class="{
                  'text-[#006086] active': isActive(item.to),
                }">
                {{ item.text }}
              </a>
            </div>
          </div>
        </div>

        <div class="hidden lg:flex lg:items-center lg:ml-4">
          <button @click="handleScheduleDemo"
            class="inline-flex items-center px-3 py-2 text-sm font-bold text-white bg-[#006086] hover:bg-[#004d6b] rounded-md shadow-sm transition-all duration-150 hover:shadow focus:outline-none transform hover:-translate-y-0.5">
            Agendar Demonstração
          </button>
        </div>

        <!-- Mobile menu button -->
        <div class="flex items-center pr-2 lg:hidden">
          <button @click="isOpen = !isOpen" type="button"
            class="inline-flex items-center justify-center p-2 rounded-md text-gray-700 hover:text-[#006086] hover:bg-gray-100 focus:outline-none transition-colors duration-150"
            aria-controls="mobile-menu" :aria-expanded="isOpen">
            <span class="sr-only">Abrir menu principal</span>
            <svg class="w-5 h-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"
              stroke="currentColor" aria-hidden="true">
              <path v-if="!isOpen" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M4 6h16M4 12h16M4 18h16" />
              <path v-else stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>
    </div>

    <!-- Mobile menu -->
    <div class="lg:hidden" id="mobile-menu" v-show="isOpen">
      <div class="pt-2 pb-3 space-y-1 bg-white border-t border-gray-200">
        <template v-for="(item, index) in menuItems" :key="index">
          <div v-if="item.hasDropdown" class="space-y-0.5">
            <button @click="toggleDropdown(index)"
              class="w-full flex items-center px-4 py-2 text-base font-medium text-gray-700 hover:text-[#006086] hover:bg-gray-100 transition-colors duration-150">
              {{ item.text }}
              <svg class="ml-auto w-3 h-3 transition-transform duration-200"
                :class="{ 'transform rotate-180': activeDropdown === index }" viewBox="0 0 20 20" fill="currentColor">
                <path fill-rule="evenodd"
                  d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                  clip-rule="evenodd" />
              </svg>
            </button>
            <div v-show="activeDropdown === index" class="py-2 space-y-1 bg-gray-50">
              <div v-if="item.isSupport" class="px-3 py-2 space-y-3">
                <RouterLink :to="item.sections[2].items.find((i) => i.isCta)?.to"
                  class="block px-4 py-3 text-sm font-bold text-gray-900 bg-kali-gold hover:bg-[#d5ac2f] rounded-md shadow transition cursor-pointer text-center"
                  @click="
                    isOpen = false;
                  closeDropdown();
                  ">
                  {{item.sections[2].items.find((i) => i.isCta)?.name}}
                </RouterLink>

                <div class="grid grid-cols-2 gap-2">
                  <component :is="btn.name.includes('WhatsApp') || btn.name.includes('Chat') ? 'a' : 'router-link'" v-for="(btn, bIndex) in item.sections[0].items.filter(
                    (i) => i.isButton,
                  )" :key="bIndex" v-bind="btn.name.includes('WhatsApp')
                    ? { href: 'https://wa.me/5579991389415?text=Ol%C3%A1%2C%20preciso%20de%20suporte', target: '_blank' }
                    : btn.name.includes('Chat')
                      ? { href: btn.to, target: '_blank', rel: 'noopener' }
                      : { to: btn.to }
                    " @click="
                          isOpen = false;
                        closeDropdown();
                        "
                    class="flex justify-center items-center px-3 py-3 text-sm font-bold text-white rounded shadow transition cursor-pointer"
                    :class="btn.buttonColor">
                    {{ btn.name }}
                  </component>
                </div>

                <div class="grid grid-cols-2 gap-2">
                  <template v-for="(
supportItem, sIndex
                    ) in item.sections[2].items.filter((i) => !i.isCta)" :key="sIndex">
                    <a v-if="supportItem.to" :href="supportItem.to" @click.prevent="navigateTo(supportItem.to)"
                      class="flex justify-center items-center px-3 py-3 text-sm font-bold text-gray-800 bg-white rounded border border-gray-200">
                      {{ supportItem.name }}
                    </a>
                    <div v-else
                      class="flex justify-center items-center px-3 py-3 text-sm font-bold text-gray-800 bg-white rounded border border-gray-200">
                      {{ supportItem.name }}
                    </div>
                  </template>
                </div>

                <div class="pt-2">
                  <h4 class="mb-2 text-sm font-bold tracking-wide text-gray-900 uppercase">
                    {{ item.sections[1].title }}
                  </h4>
                  <div class="space-y-1">
                    <div v-for="(subItem, subIndex) in item.sections[1].items" :key="subIndex">
                      <p v-if="subItem.isTitle" class="mt-2 text-sm font-semibold text-gray-800">
                        {{ subItem.name }}
                      </p>
                      <p v-else-if="subItem.isNote" class="mt-2 text-xs text-gray-500">
                        {{ subItem.name }}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <template v-else v-for="(section, sectionIndex) in item.sections" :key="sectionIndex">
                <div class="px-3 py-1.5">
                  <h4 class="mb-2 text-sm font-bold tracking-wide text-gray-900 uppercase">
                    {{ section.title }}
                  </h4>
                  <div class="space-y-0.5">
                    <template v-for="(subItem, subIndex) in section.items" :key="subIndex">
                      <RouterLink v-if="subItem.to" :to="subItem.to" @click="
                        isOpen = false;
                      closeDropdown();
                      "
                        class="block px-3 py-2 text-sm font-medium text-gray-700 hover:text-[#006086] hover:bg-gray-100 rounded transition-colors duration-150 cursor-pointer">
                        {{ subItem.name }}
                      </RouterLink>
                      <span v-else
                        class="block px-3 py-2 text-sm font-medium text-gray-700 rounded">
                        {{ subItem.name }}
                      </span>
                    </template>
                  </div>
                </div>
              </template>
            </div>
          </div>
          <RouterLink v-else :to="item.to"
            class="block px-4 py-2 text-base font-medium text-gray-700 transition-colors duration-150 hover:text-[#006086] hover:bg-[#e6f0f4]"
            :class="{
              'text-[#006086] bg-[#e6f0f4]': isActive(item.to),
            }">
            {{ item.text }}
          </RouterLink>
        </template>

        <button @click="handleScheduleDemo"
          class="block px-4 py-2 mt-4 w-[80%] mx-auto text-base font-medium text-center text-white bg-[#006086] rounded-md shadow-md transition-all duration-150 transform hover:bg-[#004d6b] hover:shadow-lg hover:-translate-y-0.5">
          Agendar Demo
        </button>
      </div>
    </div>
  </nav>
</template>

<style scoped>
@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-15px) scale(0.95);
  }

  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

@keyframes slideUp {
  from {
    opacity: 1;
    transform: translateY(0) scale(1);
  }

  to {
    opacity: 0;
    transform: translateY(-15px) scale(0.95);
  }
}

.group:hover .group-hover\:text-kali-blue-600 {
  color: #006086;
}

/* Mega menu animation */
.mega-menu-enter {
  animation: slideDown 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.mega-menu-leave {
  animation: slideUp 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

/* Underline animation for menu items */
.menu-item-underline::after {
  content: "";
  position: absolute;
  bottom: 0;
  left: 50%;
  width: 0;
  height: 4px;
  background: linear-gradient(90deg, #d4af37, #b8860b);
  transition: all 0.25s ease-out;
  transform: translateX(-50%);
}

.menu-item-underline:hover::after,
.menu-item-underline.active::after {
  width: 100%;
}

.menu-item-underline::after {
  background: #e5be35;
}
</style>
